import {resolveActiveParameterValue} from "@reva/shared/parameter-resolution";
import type {RevaValueParameterType} from "@reva/shared/reva-spec";
import equal from "fast-deep-equal";

import type {ValueIndicationRequest} from "../api/payload-types/value-indication";
import {validateParam} from "../util/objectParameterValidation";
import {abortRunningIndications} from "./indicationRequests";
import {selectCurrentIndicationPayload} from "./indicationSelectors";
import {LocationInfoEndpoint, queryLocationInfoEndpointAsync} from "./locationInfoThunk";
import {selectIsBlueHorizon} from "./objectParametersSelectors";
import {
    type BulkWritableParameterType,
    clearAllParameterConflicts,
    type ObjectParamErrorsMap,
    selectAllEnergyParams,
    selectAllFirstFinalizeParams,
    selectAllIdpEnergyCertParams,
    selectAllImmodropParams,
    selectAllInitialValuationParams,
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectAllParams,
    selectAllPlotParams,
    selectAllRegisterParams,
    selectCommentForParam,
    selectHasSpfTable,
    selectSegment,
    selectSingleParam,
    selectSingleParamError,
    setEditedValueParamErrors,
    setParamErrors,
    setParameterConflict,
    setSingleComment,
    setSingleParam,
    updateParamsForParameterType,
} from "./objectParametersSlice";
import {persistMultipleParameters, persistSegmentOverride, persistSingleParameter} from "./persistenceThunk";
import type {RevaThunkAction} from "./revaHooks";
import {selectOverviewAvmRowParameters} from "./specSelectors";
import {selectSpec} from "./specSlice";
import {selectAnyValueAvailable} from "./valueIndicationSelectors";
import {clearIndicationData, selectEditedValueDataGroup, selectLoading} from "./valueIndicationSlice";
import {queryValueIndicationWithCurrentParamsAsync} from "./valueIndicationThunk";

/**
 * Update the value of a single parameter and apply parameter validation.
 * @param parameter parameter name
 * @param parameterType the type of parameter to be used
 * @param value new value
 * @param comment new comment (optional)
 * @return update thunk
 */
const updateSingleParamThunk =
    (
        parameter: string,
        parameterType: RevaValueParameterType,
        value: any,
        comment?: string | null,
    ): RevaThunkAction<void> =>
    (dispatch) => {
        dispatch(setSingleParam({parameter, parameterType, value}));
        if (
            parameterType !== "META" &&
            parameterType !== "DOC_PROPERTY" &&
            parameterType !== "IDP_ENERGY_CERTIFICATE"
        ) {
            dispatch(setSingleComment({parameter, parameterType, comment}));
        }
    };

/**
 * Indicate or clear the current indication based on the validity of the form.
 * If the form is valid, it submits the current parameters to the backend.
 * If not, it clears the indication data if any values are available.
 * @param isValid flag indicating if the form is valid
 * @return indication thunk
 */
const indicateOrClearCurrentIndication =
    (isValid: boolean): RevaThunkAction<void> =>
    (dispatch, getState) => {
        if (isValid) {
            void dispatch(queryValueIndicationWithCurrentParamsAsync());
        } else {
            // Formular ungültig: eine noch laufende Indikation zum vorigen Parametersatz abbrechen, sonst
            // erscheint ihre Antwort als Ergebnis des jetzt unvollständigen Formulars (#19374).
            abortRunningIndications();
            // Die Loading-Prüfung deckt den Fall mit ab, dass die abgebrochene Anfrage die erste war: ohne clear
            // bliebe der Ladezustand stehen, obwohl nie eine Antwort kommt.
            const state = getState();
            if (selectAnyValueAvailable(state) || selectLoading(state)) {
                dispatch(clearIndicationData());
            }
        }
    };

/**
 * Ob sich der an die AVM gesendete Payload zwischen zwei Ständen unterscheidet (#19372). Ein Feldvergleich
 * reicht nicht: Hilfsparameter außerhalb des Spec (Modernisierungs-Scores, Gewerke-Gewichte, …) liegen nie
 * im Payload, und Spec-Parameter können ihn indirekt ändern (hasSpfTable → prop_sauna/prop_pool, register
 * überschreibt section_ii_entry_value) — deshalb hier am tatsächlichen Payload prüfen statt an der
 * Spec-Zugehörigkeit des geänderten Parameters.
 * @param before Payload vor der Änderung
 * @param after Payload nach der Änderung
 * @return true, wenn sich der Payload unterscheidet
 */
const indicationPayloadChanged = (
    before: ValueIndicationRequest | null,
    after: ValueIndicationRequest | null,
): boolean => !equal(before, after);

/**
 * Validate the form and if valid submit the current parameters to the backend
 * @return validation and submitting thunk
 */
export const validateAndSubmitThunk = (): RevaThunkAction<void> => (dispatch) => {
    const isValid = dispatch(validateFormThunk());

    dispatch(indicateOrClearCurrentIndication(isValid));
};

/**
 * Update a parameter, validate the form and submit the form to the backend
 * @param parameter parameter name
 * @param parameterType parameter type to use e.g. "AVM" or "DOC_PROPERTY
 * @param value new value
 * @param comment optional comment
 * @return update and submit thunk
 */
export const updateParamAndValidateAndSubmitFormThunk =
    (
        parameter: string,
        parameterType: RevaValueParameterType,
        value: any,
        comment?: string | null,
    ): RevaThunkAction<Promise<void> | undefined> =>
    (dispatch, getState) => {
        if (!parameter) return;

        // special case for segment parameter
        if (parameter === "segment") {
            return dispatch(updateSegmentAndValidateAndSubmitFormThunk(value, comment));
        }

        const isBlueHorizon = selectIsBlueHorizon(getState());

        // META/DOC_PROPERTY sind rein beschreibend: sie fließen nie in resolveActiveParameterValue()
        // (validateFormThunk) oder den AVM/Energy-Payload ein -- Formularvalidierung, die davon
        // abhängige Wertseiten-Rollback-Logik und das Auslösen einer Indikation sind für sie irrelevant.
        const isDescriptiveParam = parameterType === "DOC_PROPERTY" || parameterType === "META";

        // set value to "empty" if null and isBlueHorizon is true, to remove other values
        let appliedValue: any;
        switch (parameterType) {
            case "DOC_PROPERTY":
            case "ENERGY_CERT":
            case "IDP_ENERGY_CERTIFICATE":
            case "REGISTER":
            case "PLOT":
            case "INSPECTION_DERIVED":
            case "FIRST_FINALIZE":
            case "META":
                appliedValue = value;
                break;

            case "AVM":
                appliedValue = value === null && isBlueHorizon && comment ? "empty" : value;
                break;

            default:
                console.warn(
                    `updateParamAndValidateAndSubmitFormThunk: unknown parameterType ${parameterType} for parameter ${parameter}`,
                );
                appliedValue = undefined;
                break;
        }

        const state = getState();
        const currentParam = selectSingleParam(state, parameter, parameterType);
        const selectComment = selectCommentForParam(state, parameter, parameterType);
        const validateBeforePersist = !isDescriptiveParam && selectEditedValueDataGroup(state) != null;
        const initialIndicationPayload = isDescriptiveParam ? null : selectCurrentIndicationPayload(state);
        const paramError = selectSingleParamError(state, parameter);

        dispatch(updateSingleParamThunk(parameter, parameterType, appliedValue, comment));
        const isValid = isDescriptiveParam ? true : dispatch(validateFormThunk());

        if (validateBeforePersist) {
            if (!isValid && currentParam !== appliedValue) {
                dispatch(setEditedValueParamErrors({errors: selectSingleParamError(getState(), parameter)}));
                dispatch(updateSingleParamThunk(parameter, parameterType, currentParam, selectComment));
            } else {
                dispatch(setEditedValueParamErrors(null));
            }
        }

        const valueChanged = parameterType !== "META" && currentParam !== appliedValue;
        const commentChanged = (selectComment || comment) && selectComment !== comment;

        // ein Kommentar kann das Formular nicht ungültig machen: eine reine Kommentaränderung (kein
        // Wertwechsel) wird daher immer persistiert, unabhängig von validateBeforePersist (#19378)
        if (
            ((valueChanged || (paramError?.length ?? 0) > 0) && (isValid || !validateBeforePersist)) ||
            (!valueChanged && commentChanged)
        ) {
            // returned so callers can await persistence completion before firing a second write
            const persistPromise = dispatch(persistSingleParameter(parameter, parameterType, appliedValue, comment));

            //#18881 / #19165: Änderung von location_index aktualisiert Micro/Macro Texte (description-Endpunkt)
            if (parameter === "location_index" && currentParam !== appliedValue) {
                dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.DESCRIPTION));
            }

            const payloadChanged =
                !isDescriptiveParam &&
                indicationPayloadChanged(initialIndicationPayload, selectCurrentIndicationPayload(getState()));

            if (
                payloadChanged ||
                // had previous error, indicate again so avm errors are not lost
                (paramError?.length ?? 0) > 0
            ) {
                dispatch(indicateOrClearCurrentIndication(isValid));
            }

            return persistPromise;
        }
    };

/**
 * Kommentar-Map, die zu allen übergebenen Parametern den Kommentar löscht. Das Einzel-Update
 * (updateParamAndValidateAndSubmitFormThunk mit comment=null) hat das implizit über
 * updateSingleParamThunk getan; beim Sammel-Update muss es explizit mitgegeben werden.
 * @param params Parameter, deren Kommentare gelöscht werden sollen
 * @return Kommentar-Map für updateMultipleParamsAndValidateAndSubmitFormThunk
 */
export const clearedComments = (params: Record<string, any>): Record<string, null> =>
    Object.fromEntries(Object.keys(params).map((parameter) => [parameter, null]));

/**
 * Thunk to update multiple parameters of the same type, validate the form, persist,
 * and trigger indication — analogous to updateParamAndValidateAndSubmitFormThunk.
 * Gleiche Semantik wie das Einzel-Update (#19372): Nur während der Wertseiten-Bearbeitung wird ein
 * ungültig machender Stand zurückgesetzt und nicht gespeichert; sonst bleibt die Eingabe auch bei
 * ungültigem Formular (z.B. fehlende Wohnfläche) erhalten und wird persistiert, damit der Nutzer
 * weiter erfassen kann. Bei ungültigem Formular wird höchstens eine vorhandene Indikation geleert,
 * nicht neu gerechnet.
 * @param parameterType e.g. "INSPECTION_DERIVED"
 * @param params parameter name → new value
 * @param comments optional parameter name → comment (null löscht den Kommentar)
 * @return update, validate and submit thunk
 */
export const updateMultipleParamsAndValidateAndSubmitFormThunk =
    (
        parameterType: BulkWritableParameterType,
        params: Record<string, any>,
        comments?: Record<string, string | null>,
    ): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const state = getState();

        const previousValues = Object.fromEntries(
            Object.keys(params).map((p) => [p, selectSingleParam(state, p, parameterType)]),
        );
        const previousComments = Object.fromEntries(
            Object.keys(params).map((p) => [p, selectCommentForParam(state, p, parameterType)]),
        );
        const hadAnyError = Object.keys(params).some((p) => (selectSingleParamError(state, p)?.length ?? 0) > 0);
        const validateBeforePersist = selectEditedValueDataGroup(state) != null;
        const initialIndicationPayload = selectCurrentIndicationPayload(state);

        dispatch(updateParamsForParameterType({parameterType, params}));
        Object.entries(comments ?? {}).forEach(([parameter, comment]) => {
            dispatch(setSingleComment({parameter, parameterType: parameterType, comment}));
        });
        const isValid = dispatch(validateFormThunk());

        const anyValueChanged = Object.entries(params).some(([p, v]) => previousValues[p] !== v);
        const anyCommentChanged = Object.entries(comments ?? {}).some(
            ([p, c]) => (previousComments[p] || c) && previousComments[p] !== c,
        );

        if (validateBeforePersist) {
            if (!isValid && anyValueChanged) {
                const errors = Object.keys(params).flatMap((p) => selectSingleParamError(getState(), p) ?? []);
                // leeres Array wie im Einzel-Update als null ablegen (#19372)
                dispatch(setEditedValueParamErrors(errors.length ? {errors} : null));
                dispatch(updateParamsForParameterType({parameterType, params: previousValues}));
                Object.entries(previousComments).forEach(([parameter, comment]) => {
                    dispatch(setSingleComment({parameter, parameterType: parameterType, comment}));
                });
            } else {
                dispatch(setEditedValueParamErrors(null));
            }
        }

        // ein Kommentar kann das Formular nicht ungültig machen: eine reine Kommentaränderung (kein
        // Wertwechsel) wird daher immer persistiert, unabhängig von validateBeforePersist (#19378)
        if (
            ((anyValueChanged || hadAnyError) && (isValid || !validateBeforePersist)) ||
            (!anyValueChanged && anyCommentChanged)
        ) {
            void dispatch(persistMultipleParameters(params, parameterType, comments));

            if (
                indicationPayloadChanged(initialIndicationPayload, selectCurrentIndicationPayload(getState())) ||
                hadAnyError
            ) {
                dispatch(indicateOrClearCurrentIndication(isValid));
            }
        }
    };

/**
 * Setzt den Segment-Override der Überblick-Schicht und übernimmt, was das Backend daraus macht
 * (#19444). `null` nimmt den Override zurück; das effektive Segment folgt dann der Kette.
 *
 * Der Override wird optimistisch gesetzt, bevor der Request rausgeht — sonst hätte der
 * Kommentar-Dialog, den eine Kommentarpflicht öffnet, den gewählten Wert nicht mehr zur Hand. Welche
 * Schichten ein Wechsel verwirft, entscheidet das Backend; deshalb wird erst nach dem Anwenden der
 * Antwort validiert und gerechnet, nicht vorher.
 * @param value new segment value, or null to reset the override
 * @param comment optional comment
 * @return update and submit thunk
 */
export const updateSegmentAndValidateAndSubmitFormThunk =
    (value: string | null, comment?: string | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const segmentBefore = selectSegment(getState());

        dispatch(updateSingleParamThunk("segment", "AVM", value, comment));
        await dispatch(persistSegmentOverride(value, comment ?? null, segmentBefore));

        const isValid = dispatch(validateFormThunk());
        dispatch(indicateOrClearCurrentIndication(isValid));
    };

/**
 * Validate the form (all inputs for the current segment) and set errors in the state
 * @return whether the form is valid
 */
export const validateFormThunk = (): RevaThunkAction<boolean> => (dispatch, getState) => {
    const state = getState();

    const spec = selectSpec(state);
    const segment = selectSegment(state);
    const allParams = selectAllParams(state);
    const initialValuationParams = selectAllInitialValuationParams(state);
    const inspectionParams = selectAllInspectionParams(state);
    const inspectionDerivedParams = selectAllInspectionDerivedParams(state);
    const plotParams = selectAllPlotParams(state);
    const energyParams = selectAllEnergyParams(state);
    const idpEnergyCertParams = selectAllIdpEnergyCertParams(state);
    const registerParams = selectAllRegisterParams(state);
    const firstFinalizeParams = selectAllFirstFinalizeParams(state);
    const immodropParams = selectAllImmodropParams(state);
    const hasSpfTable = selectHasSpfTable(state);
    const overviewAvmRowParameters = selectOverviewAvmRowParameters(state);

    if (!spec || !segment) {
        return false;
    }

    const paramsInSegment = Object.entries(spec.apiParams?.VALUE?.[segment] || {});

    const errors: ObjectParamErrorsMap = {};

    // vor dem Neuvalidieren alle letzten Konflikte löschen
    dispatch(clearAllParameterConflicts());

    for (const [param, parameterData] of paramsInSegment) {
        const reva = allParams[param];
        const initialValuation = initialValuationParams[param];
        const inspection = inspectionParams[param];
        const inspectionDerived = inspectionDerivedParams[param];
        const plot = plotParams[param];
        const register = registerParams[param];
        const energy = energyParams[param];
        const idpEnergyCertificate = idpEnergyCertParams[param];
        const firstFinalize = firstFinalizeParams[param];
        const immodrop = immodropParams[param];

        const result = resolveActiveParameterValue("AVM", {
            reva,
            initialValuation,
            inspection,
            inspectionDerived,
            plot,
            register,
            energy,
            idpEnergyCertificate,
            firstFinalize,
            immodrop,
            hasSpfTable,
            useConflictingValue: true,
            parameter: param,
        });

        const value = result.activeParameterValue;
        // #19774: a conflict without an Overview row has no place to be resolved, so it does not block
        if (result.hasConflict && (overviewAvmRowParameters == null || overviewAvmRowParameters.has(param))) {
            // console.log("validateFormThunk: Parameter has conflict:", param, value, result, selectByLanguage(i18n.language, parameterData.nameDe, parameterData.nameEn));
            dispatch(
                setParameterConflict({
                    parameter: param,
                }),
            );
        }

        const paramErrors = validateParam(value, parameterData);

        if (paramErrors.length > 0) {
            errors[param] = paramErrors;
        }
    }

    dispatch(
        setParamErrors({
            errors: errors,
        }),
    );

    return Object.keys(errors).length === 0; // Return whether the form is valid
};
